import React from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import IconDelete from '../IconDelete/IconDelete';
import FieldCurrencyInput from '../FieldCurrencyInput/FieldCurrencyInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { required } from '../../util/validators';
import appSettings from '../../config/settings';
import { useConfiguration } from '../../context/configurationContext';
import FieldPlusMinusToggle from '../FieldPlusMinusToggle/FieldPlusMinusToggle';
import { uniqueId } from 'lodash';

import css from './FieldSpecialRequest.module.css';
import classNames from 'classnames';

const FieldSpecialRequests = props => {
  const { className } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const marketplaceCurrency = config?.currency;

  const priceRequired = required(
    intl.formatMessage({ id: 'EditListingPricingForm.priceRequired' })
  );

  const nameRequired = required(intl.formatMessage({ id: 'FieldSpecialRequests.nameRequired' }));

  return (
    <FieldArray name="specialRequests">
      {({ fields }) => (
        <div className={classNames(css.container, className)}>
          <div className={css.headerRow}>
            <h2 className={css.title}>
              <FormattedMessage id="FieldSpecialRequests.title" />
            </h2>

            <button
              type="button"
              onClick={() =>
                fields.insert(0, {
                  name: '',
                  description: '',
                  sign: 'add',
                  price: null,
                  id: uniqueId(`${Date.now()}`),
                })
              }
              className={css.addButton}
            >
              <FormattedMessage id="FieldSpecialRequests.addRequest" />
            </button>
          </div>

          <div className={css.list}>
            {fields.map((name, index) => {
              const item = fields.value?.[index];
              const key = item?.id || name;

              return (
                <div key={key} className={css.card}>
                  <div className={css.cardInner}>
                    <div className={css.leftCol}>
                      <Field name={`${name}.name`}>
                        {({ input }) => (
                          <div className={css.fieldBlock}>
                            <label className={css.label}>
                              <FormattedMessage id="FieldSpecialRequests.requestName" />
                            </label>

                            <FieldTextInput
                              {...input}
                              placeholder={intl.formatMessage({
                                id: 'FieldSpecialRequests.requestNamePlaceholder',
                              })}
                              className={css.textInput}
                              validate={nameRequired}
                            />
                          </div>
                        )}
                      </Field>

                      <Field name={`${name}.description`}>
                        {({ input }) => (
                          <div className={css.fieldBlock}>
                            <label className={css.label}>
                              <FormattedMessage id="FieldSpecialRequests.descriptionOptional" />
                            </label>

                            <FieldTextInput
                              {...input}
                              placeholder={intl.formatMessage({
                                id: 'FieldSpecialRequests.descriptionPlaceholder',
                              })}
                              rows={2}
                              className={css.textarea}
                              type="textarea"
                            />
                          </div>
                        )}
                      </Field>

                      <div className={css.fieldBlock}>
                        <label className={css.label}>
                          <FormattedMessage id="FieldSpecialRequests.price" />
                        </label>

                        <div className={css.priceRowWithDesc}>
                          <div className={css.priceRow}>
                            <FieldPlusMinusToggle name={`${name}.sign`} />

                            <FieldCurrencyInput
                              id={`${name}.price`}
                              name={`${name}.price`}
                              className={classNames(css.input, css.inputWidthAuto)}
                              placeholder={intl.formatMessage({
                                id: 'EditListingPricingForm.priceInputPlaceholder',
                              })}
                              currencyConfig={appSettings.getCurrencyFormatting(
                                marketplaceCurrency
                              )}
                              validate={priceRequired}
                              autoComplete="off"
                            />
                          </div>

                          <p className={css.hint}>
                            <FormattedMessage id="FieldSpecialRequests.priceHint" />
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => fields.remove(index)}
                      className={css.removeButton}
                    >
                      <IconDelete />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </FieldArray>
  );
};

export default FieldSpecialRequests;
